/**
 * Downgrade Warning Dialog
 * Shown when user downgrades and exceeds storage limit
 * Warns user they have 3 days to reduce storage before automatic suspension
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, X, Trash2, Clock, ArrowUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@config/constants';
import { supabase } from '@config/supabase';
import { useAuthStore } from '@stores/useAuthStore';
import { ModalShell } from '@components/ui/ModalShell';

interface DowngradeWarningDialogProps {
  open: boolean;
  onClose: () => void;
  storageUsed: number;
  storageLimit: number;
  daysRemaining: number;
}

export const DowngradeWarningDialog: React.FC<DowngradeWarningDialogProps> = ({
  open,
  onClose,
  storageUsed,
  storageLimit,
  daysRemaining,
}) => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [isDismissed, setIsDismissed] = useState(false);

  // Listen for reopen event
  useEffect(() => {
    const handleReopen = () => {
      setIsDismissed(false);
    };

    window.addEventListener('downgrade-warning-dialog-reopen', handleReopen);
    return () => {
      window.removeEventListener('downgrade-warning-dialog-reopen', handleReopen);
    };
  }, []);

  // Format bytes to human readable
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const excessStorage = storageUsed - storageLimit;
  const excessGB = (excessStorage / (1024 * 1024 * 1024)).toFixed(2);

  const handleGoToCloudExplorer = () => {
    setIsDismissed(true);
    onClose();
    navigate(ROUTES.CLOUD_EXPLORER);
  };

  const handleUpgradePlan = () => {
    setIsDismissed(true);
    onClose();
    navigate(ROUTES.BILLING);
  };

  const handleDismiss = async () => {
    // Mark dialog as dismissed in database
    if (user?.id) {
      try {
        await supabase
          .from('profiles')
          .update({ downgrade_warning_dismissed: true })
          .eq('id', user.id);
      } catch (error) {
        console.error('Failed to mark warning as dismissed:', error);
      }
    }
    setIsDismissed(true);
    onClose();
  };

  // Reset dismissed state when dialog is opened
  useEffect(() => {
    if (open) {
      setIsDismissed(false);
    }
  }, [open]);

  if (!open || isDismissed) {
    return null;
  }

  return (
    <ModalShell
      onClose={handleDismiss}
      open={open && !isDismissed}
      panelClassName="max-w-2xl w-full bg-white rounded-lg max-h-[90dvh] animate-slide-in-up"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto animate-slide-in-up">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Storage Limit Exceeded After Downgrade
            </h2>
          </div>
          <button
            onClick={handleDismiss}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Warning Message */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800 font-medium">
              ⚠️ Your account has exceeded the storage limit after downgrading your plan.
            </p>
          </div>

          {/* Storage Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Storage Used:</span>
              <span className="text-sm font-semibold text-gray-900">{formatBytes(storageUsed)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Storage Limit:</span>
              <span className="text-sm font-semibold text-gray-900">{formatBytes(storageLimit)}</span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-gray-200">
              <span className="text-sm font-medium text-red-600">Excess Storage:</span>
              <span className="text-sm font-bold text-red-600">+{excessGB} GB</span>
            </div>
          </div>

          {/* Timeline Visualization */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-900">
                  {daysRemaining === 0 
                    ? '⚠️ Last day - Account will be suspended tomorrow'
                    : `You have ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} to reduce your storage`}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  After {daysRemaining === 0 ? 'today' : `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'}`}, your account will be automatically suspended.
                </p>
              </div>
            </div>

            {/* Visual Timeline */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">What happens next:</h4>
              <div className="space-y-3">
                {/* Phase 1: Current (Warning) */}
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 ${daysRemaining > 0 ? 'bg-blue-600' : 'bg-gray-400'}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${daysRemaining > 0 ? 'text-blue-900' : 'text-gray-500'}`}>
                        Phase 1: Warning Period
                      </span>
                      {daysRemaining > 0 && (
                        <span className="text-xs text-blue-600 font-medium">(Current)</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {daysRemaining > 0 
                        ? `${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining to delete files`
                        : 'Completed - Account will be suspended'}
                    </p>
                  </div>
                </div>

                {/* Phase 2: Suspension with Download */}
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-1.5 ${daysRemaining === 0 ? 'bg-amber-600' : 'bg-gray-300'}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-semibold ${daysRemaining === 0 ? 'text-amber-900' : 'text-gray-500'}`}>
                        Phase 2: Account Suspended
                      </span>
                      {daysRemaining === 0 && (
                        <span className="text-xs text-amber-600 font-medium">(Next)</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Account suspended • 14 days to download your data • Uploads blocked
                    </p>
                  </div>
                </div>

                {/* Phase 3: Deletion */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-2 h-2 rounded-full mt-1.5 bg-red-300" />
                  <div className="flex-1">
                    <span className="text-xs font-semibold text-gray-500">Phase 3: Account Deletion</span>
                    <p className="text-xs text-red-600 mt-0.5 font-medium">
                      Account and all data permanently deleted (no recovery possible)
                    </p>
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              {daysRemaining > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Time remaining in Phase 1</span>
                    <span className="text-xs font-semibold text-blue-600">{daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${((3 - daysRemaining) / 3) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Solutions */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-900">How to prevent suspension:</h3>
            
            {/* Option 1: Upgrade Plan */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <ArrowUp className="w-4 h-4 text-blue-600" />
                <h4 className="text-sm font-semibold text-blue-900">Option 1: Upgrade Your Plan</h4>
              </div>
              <p className="text-xs text-blue-800">
                Upgrade to a plan with more storage to keep all your files. This is the quickest solution!
              </p>
              <button
                onClick={handleUpgradePlan}
                className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg"
              >
                <ArrowUp className="w-4 h-4" />
                Upgrade Plan Now
              </button>
            </div>

            {/* Option 2: Delete Files */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2">
                <Trash2 className="w-4 h-4 text-green-600" />
                <h4 className="text-sm font-semibold text-green-900">Option 2: Delete Files</h4>
              </div>
              <p className="text-xs text-green-800">
                Delete files to reduce storage below <span className="font-semibold">{formatBytes(storageLimit)}</span>
              </p>
              <ol className="list-decimal list-inside space-y-1 text-xs text-green-700 ml-4 mt-1">
                <li>Go to Cloud Explorer and delete unnecessary files</li>
                <li>Delete at least {excessGB} GB to get back under your limit</li>
                <li>Once storage is below limit, the warning will automatically disappear</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            I understand
          </button>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleUpgradePlan}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg"
            >
              <ArrowUp className="w-4 h-4" />
              Upgrade Plan
            </button>
            <button
              onClick={handleGoToCloudExplorer}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium text-sm transition-all shadow-md hover:shadow-lg"
            >
              <Trash2 className="w-4 h-4" />
              Delete Files
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
};

/**
 * Function to reopen the downgrade warning dialog
 * Can be called from anywhere (e.g., Header button)
 */
export const reopenDowngradeWarningDialog = () => {
  window.dispatchEvent(new CustomEvent('downgrade-warning-dialog-reopen'));
};

